<script lang="ts" setup>
	import type { IconLibProps } from '../tools/icon.vue'

	const props = withDefaults(
		defineProps<{
			title?: string
			description?: string
			type?: 'base' | 'auto' | 'health' | 'travel' | 'gadget' | 'individual'
		}>(),
		{
			type: 'base',
			title: 'Insurance made easy for your business',
			description:
				"Co-create custom insurance products made for your business' needs. Contact one of our experts to help on how MyCoverGenius can transform your insurance experience."
		}
	)
	defineEmits<{
		(event: 'secondaryClick'): void
	}>()
	const cardColors = computed(() => {
		const colorPallete = {
			base: 'bg-main',
			health: 'bg-health-100',
			auto: 'bg-auto-100',
			travel: 'bg-travel-100',
			gadget: 'bg-gadget-100'
		} as Record<string, string>
		return colorPallete?.[props?.type] ?? 'bg-main'
	})
	const effectColors = computed(() => {
		const colorPallete = {
			base: 'bg-main',
			health: 'bg-health-500',
			auto: 'bg-auto-500',
			travel: 'bg-travel-500',
			gadget: 'bg-gadget-500'
		} as Record<string, string>
		return colorPallete?.[props?.type] ?? 'bg-main'
	})
	const tagIcon = computed<IconLibProps | null>(() => {
		const dir = 'random'
		const colorPallete: Record<string, IconLibProps> = {
			health: {
				icon: 'health-tag',
				dir
			},
			auto: {
				icon: 'auto-tag',
				dir
			},
			travel: {
				icon: 'travel-tag',
				dir
			},
			gadget: {
				icon: 'gadget-tag',
				dir
			},
			individual: {
				icon: 'individual-tag',
				dir
			}
		}
		return colorPallete?.[props.type] ?? null
	})
	const productImageEffect = computed(() => {
		const imgPallete = {
			base: '/v1727094994/insurance-tailored_ti4ggi.webp',
			health: '/v1727082976/health-coverage-img_wfm5to.webp',
			auto: '/v1727082979/auto-coverage-img_tkfdli.webp',
			travel: '/v1727082966/travel-coverage-img_bt8pdx.webp',
			gadget: '/v1727082973/gadget-coverage-img_h3b4th.webp'
		} as Record<string, string>
		return imgPallete?.[props?.type] ?? 'random/health-coverage'
	})
</script>

<template>
	<ui-pad :ui="{ paddingBlock: '', paddingInline: 'px-0 md:px-10' }">
		<section class="relative overflow-hidden min-h-[580px] py-5 bg-gray-900 sm:rounded-[20px] w-full flexed">
			<div
				class="relative z-10 min-h-[inherit] justify-center sm:justify-between flex items-center w-full px-5 sm:px-10 xl:pt-0 pt-10 flex-wrap flex-col md:flex-row gap-y-5 sm:gap-y-10"
			>
				<article class="max-w-[589px] w-full relative md:mr-16 xl:mr-0">
					<span v-if="tagIcon" class="pb-3 block">
						<tools-icon v-bind="tagIcon" width="42" height="32" />
					</span>
					<h3
						class="text-white dark:text-gray-100 maitree-bold lg:text-[3.25rem]/[4.55rem] md:text-[2.625rem]/[3.15rem] text-[2rem]/[2.4rem]"
					>
						<slot name="title">
							{{ title }}
						</slot>
					</h3>

					<p
						v-show="type === 'base' || 'individual'"
						class="md:text-lg lg:text-xl text-white font-normal pt-5 text-base"
					>
						<slot name="description">
							{{ description }}
						</slot>
					</p>
					<span
						v-show="type !== 'individual'"
						class="absolute"
						:class="{ '-right-3': type === 'base', 'right-24': type !== 'base' }"
					>
						<tools-icon
							dir="arrows"
							icon="bent-arrow"
							path-class="fill-animate custom-transition-3"
							svg-class="h-[100.57px] w-[55.94px] sm:h-[106.36px] sm:w-[auto]"
						/>
					</span>

					<div class="flex items-center gap-3 mt-10 relative z-10 flex-wrap">
						<slot name="button-group">
							<button
								type="button"
								:aria-describedby="type !== 'base' ? 'See more products' : 'Book a demo'"
								class="default-btn sm:text-base active:scale-95 flex-none bg-white text-[#1A1200] rounded-[40px] min-h-[56px] font-medium justify-center p-[18px_20px] sm:p-[20px_52px]"
								:name="type !== 'base' ? 'See more products' : 'Book a demo'"
								@click="$emit('secondaryClick')"
							>
								{{ type !== 'base' ? 'See more products' : 'Get Covered' }}
							</button>

							<button
								v-if="type === 'base'"
								type="button"
								class="default-btn sm:text-base p-[18px_20px] sm:p-[20px_52px] group custom-transition-3 sm:border-none border flex-none sm:bg-gray-800 hover:bg-white text-white hover:text-[#1A1200] rounded-[40px] min-h-[56px] font-medium justify-center"
								id="intercom_custom_btn"
							>
								<tools-icon
									dir="line"
									icon="call"
									path-class="group-hover:fill-[#1A1200] custom-transition-3"
								/>
								Chat with us
							</button>
						</slot>
					</div>
				</article>
				<ui-spacer :ui="{ marginBlock: 'mt-8' }" />
				<slot name="image-wrapper">
					<ui-bent-card
						v-if="!['base'].includes(type)"
						:ui="{
							backgrounContainer: { bg: cardColors },
							base: 'sm:mt-5 border  xl:mx-0 sm:max-w-[300px]  xl:max-w-full   w-full',
							element: { base: type !== 'base' ? 'rotate-6' : '', width: '' }
						}"
					>
						<div class="w-full mx-auto max-w-[400px] md:max-w-[420px] h-full">
							<slot name="image">
								<ui-vertical-scroll-effect class="block relative" :class="effectColors">
									<nuxt-img
										v-for="(_, index) in 2"
										:key="index"
										provider="cloudinary"
										src="/v1727082972/health-coverage_gpptol.png"
										loading="lazy"
										:alt="`${type} image scroll effect`"
										class="object-contain h-full w-fit mb-10"
									/>
								</ui-vertical-scroll-effect>
							</slot>
						</div>
					</ui-bent-card>
					<nuxt-img
						v-else
						provider="cloudinary"
						:src="productImageEffect"
						loading="lazy"
						alt="product image highlight"
						class="sm:max-h-[542.56px] max-h-[335px] relative md:-bottom-10 -bottom-5"
						fit="fill"
					/>
				</slot>
			</div>

			<div class="absolute pointer-events-none top-0">
				<tools-icon dir="random" icon="big-wave" />
			</div>
		</section>
	</ui-pad>
</template>

<style>
	.fill-animate {
		animation: fill 2.5s linear infinite;
		stroke-dasharray: 4;
	}
	@keyframes fill {
		from {
			stroke: white;
		}
		to {
			stroke: #ffb908;
			stroke-dasharray: 5;
		}
	}
</style>
